import { FC, useMemo } from 'react'

import { buildSsoAuthorizeUrl } from 'components/contentspace/portal/autoLogin'
import { IPublicSsoConfig, PortalSsoProtocol } from 'components/contentspace/types'
import { testProps } from 'utils/test/qaData'

interface ISsoLoginProps {
  companyId: string
  portalId: string
  ssoConfigs: IPublicSsoConfig[]
  returnUrl?: string
}

const PROTOCOL_ICONS: Partial<Record<PortalSsoProtocol, string>> = {
  GOOGLE: 'https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg',
  MICROSOFT:
    'https://learn.microsoft.com/en-us/entra/identity-platform/media/howto-add-branding-in-apps/ms-symbollockup_mssymbol_19.svg',
}

const PROTOCOL_LABELS: Partial<Record<PortalSsoProtocol, string>> = {
  GOOGLE: 'Google',
  MICROSOFT: 'Microsoft',
}

export const SsoLogin: FC<ISsoLoginProps> = ({ companyId, portalId, ssoConfigs, returnUrl }) => {
  const enabledConfigs = useMemo(() => ssoConfigs.filter((c) => c.enabled), [ssoConfigs])

  if (enabledConfigs.length === 0) {
    return (
      <div style={{ textAlign: 'center', color: 'var(--brand-secondaryTextColor)' }}>
        <p>SSO is not configured for this portal.</p>
      </div>
    )
  }

  const handleSsoClick = (configItem: IPublicSsoConfig) => {
    if (configItem.protocol === 'TOKEN_BRIDGE' && configItem.remoteLoginUrl) {
      const loginUrl = new URL(configItem.remoteLoginUrl)
      if (loginUrl.protocol !== 'https:' && loginUrl.protocol !== 'http:') {
        return
      }
      const nextUrl = returnUrl || window.location.href
      loginUrl.searchParams.set('next', nextUrl)
      window.location.replace(loginUrl.toString())
    } else {
      window.location.replace(
        buildSsoAuthorizeUrl(companyId, portalId, configItem.id, returnUrl, { manual: true }),
      )
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {enabledConfigs.map((ssoConfig) => {
        const icon = PROTOCOL_ICONS[ssoConfig.protocol]
        const protocolLabel = PROTOCOL_LABELS[ssoConfig.protocol]
        const buttonLabel = protocolLabel
          ? `Continue with ${protocolLabel}`
          : `Continue with ${ssoConfig.displayName}`

        const isGoogle = ssoConfig.protocol === 'GOOGLE'
        const isMicrosoft = ssoConfig.protocol === 'MICROSOFT'
        const isBranded = isGoogle || isMicrosoft

        return (
          <button
            key={ssoConfig.id}
            onClick={() => handleSsoClick(ssoConfig)}
            {...testProps({ el: 'button', name: 'ssoProvider', value: ssoConfig.id })}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              width: '100%',
              padding: '12px 24px',
              borderRadius: '8px',
              // Google/Microsoft sign-in buttons follow vendor brand-spec
              // (white #fff bg, #dadce0 border, #3c4043 text) — these are
              // mandatory per Google sign-in branding guidelines, so they
              // intentionally bypass the portal brand palette.
              border: isBranded ? '1px solid #dadce0' : '1px solid var(--brand-primaryColor)',
              backgroundColor: isBranded ? '#fff' : 'var(--brand-primaryColor)',
              color: isBranded ? '#3c4043' : 'var(--brand-lightTextColor)',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background-color 0.2s, box-shadow 0.2s',
            }}
            type='button'
          >
            {icon && <img alt={buttonLabel} height={20} src={icon} width={20} />}
            {buttonLabel}
          </button>
        )
      })}
    </div>
  )
}
